import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { useAuth } from "@/context/AuthContext";
import { ThemeToggle, TAGLINE } from "@/components/Layout";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { ArrowLeft } from "lucide-react";
import { toast } from "sonner";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    const res = await login(email.trim(), password);
    setLoading(false);
    if (res.ok) {
      toast.success(`Welcome back, ${res.user.name}`);
      const dest = res.user.role === "super_admin"
        ? "/admin"
        : res.user.role === "l2_admin"
          ? "/ops"
          : "/institution";
      navigate(dest);
    } else {
      toast.error(res.error || "Login failed");
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-950 flex flex-col">
      <header className="w-full max-w-7xl mx-auto px-6 md:px-12 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 text-sm text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white" data-testid="login-back-link">
          <ArrowLeft className="w-4 h-4" /> Back to CertiSeal
        </Link>
        <ThemeToggle />
      </header>

      <div className="flex-1 flex items-center justify-center px-6 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
          className="w-full max-w-md"
        >
          <div className="text-center mb-10">
            <img src="/certiseal-logo.png" alt="CertiSeal" className="w-16 h-16 mx-auto mb-5 rounded-full dark:bg-white dark:p-1" />
            <h1 className="font-display font-extrabold text-3xl md:text-4xl tracking-tight text-slate-900 dark:text-white">Admin Sign in</h1>
            <p className="mt-2 text-slate-600 dark:text-slate-400">{TAGLINE}.</p>
          </div>

          <div className="bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-sm p-8">
            <form onSubmit={handleSubmit} className="space-y-5">
              <div>
                <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Email</label>
                <Input
                  type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                  placeholder="admin@certiseal.io" required
                  className="h-12 border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  data-testid="login-email-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-600 dark:text-slate-300 mb-2 block">Password</label>
                <Input
                  type="password" value={password} onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••" required
                  className="h-12 border-slate-200 dark:border-slate-700 dark:bg-slate-950 dark:text-white"
                  data-testid="login-password-input"
                />
              </div>
              <Button
                type="submit" disabled={loading}
                className="w-full h-12 bg-blue-600 hover:bg-blue-700 active:scale-[0.98] font-display font-semibold"
                data-testid="login-submit-btn"
              >
                {loading ? "Signing in…" : "Sign in"}
              </Button>
              <div className="text-center">
                <Link
                  to="/forgot-password"
                  className="text-sm text-slate-500 dark:text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 underline-offset-4 hover:underline transition-colors"
                  data-testid="login-forgot-link"
                >
                  Forgot password?
                </Link>
              </div>
            </form>
          </div>

          <p className="mt-8 text-center text-sm text-slate-500 dark:text-slate-400">
            Need an institution account? Contact your super administrator.
          </p>
        </motion.div>
      </div>
    </div>
  );
}
